// src/components/layout/Header.jsx
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  Menu,
  X,
  ChevronDown,
  User,
  LogOut,
  Settings,
  Award,
  BookOpen,
  Sparkles,
  Home,
  Target,
  Users,
  Feather
} from 'lucide-react';
import Button from '../shared/Button';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { Capacitor } from '@capacitor/core';

const Header = ({ scrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logOut, redirectToAuth } = useAuth();
  const { assessmentResults } = useApp();
  const [resourcesOpen, setResourcesOpen] = useState(false);

  const navigation = [
    { name: 'Assessment', href: '/assessment', icon: Target },
    { name: 'Dive In', href: '/modules', icon: BookOpen },
    { name: 'Toolkit', href: '/toolkit', icon: Sparkles },
    { name: 'Dashboard', href: '/dashboard', icon: Award },
  ];

  // Journal lives under Resources so the top bar stays uncrowded
  const resourcesMenu = [
    { name: 'Resource Library', href: '/resources', icon: MessageSquare },
    { name: 'The Philosopher\'s Journal', href: '/journal', icon: Feather },
    { name: 'The Lineage', href: '/lineage', icon: Sparkles },
  ];
  const resourcesActive = ['/resources', '/journal', '/lineage'].includes(location.pathname);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-gunmetal/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <nav className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/profound-philosophy-logo.webp"
              alt="Profound Philosophy"
              className="w-16 h-16 group-hover:scale-110 transition-transform flex-shrink-0"
            />
            <div className="hidden sm:flex flex-col">
              {/* Not a heading: every page owns its h1, and this block is
                  display:none under sm: a viewport-dependent h1 breaks the
                  document outline. */}
              <p className="text-xl font-bold font-secondary text-white group-hover:text-bright-gold transition-colors" style={{ lineHeight: '1.2' }}>
                Profound Philosophy
              </p>
              <p className="text-xs text-muted-gold group-hover:text-gold transition-colors" style={{ lineHeight: '1', marginTop: '2px' }}>A Dive Deep Dialogues App</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    isActive 
                      ? 'bg-gold/20 text-bright-gold' 
                      : 'text-pearl hover:text-bright-gold hover:bg-gunmetal/20'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium whitespace-nowrap">{item.name}</span>
                </Link>
              );
            })}

            {/* Resources dropdown: holds the library and the Journal */}
            <div
              className="relative"
              onMouseEnter={() => setResourcesOpen(true)}
              onMouseLeave={() => setResourcesOpen(false)}
              onKeyDown={(e) => { if (e.key === 'Escape') setResourcesOpen(false); }}
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setResourcesOpen(false); }}
            >
              <button
                onClick={() => setResourcesOpen(!resourcesOpen)}
                aria-haspopup="true"
                aria-expanded={resourcesOpen}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                  resourcesActive
                    ? 'bg-gold/20 text-bright-gold'
                    : 'text-pearl hover:text-bright-gold hover:bg-gunmetal/20'
                }`}
              >
                <MessageSquare className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium whitespace-nowrap">Resources</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {resourcesOpen && (
                <div className="absolute right-0 top-full pt-2 w-64">
                  <div className="rounded-xl border border-slate-700 bg-slate-900/95 backdrop-blur-md shadow-xl overflow-hidden">
                    {resourcesMenu.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setResourcesOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                            isActive
                              ? 'bg-gold/20 text-bright-gold'
                              : 'text-pearl hover:text-bright-gold hover:bg-gunmetal/40'
                          }`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="font-medium whitespace-nowrap">{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <>
                {/* User Progress Badge */}
                {assessmentResults && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 rounded-full">
                    <Award className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-green-300">
                      {assessmentResults.archetype || 'Explorer'}
                    </span>
                  </div>
                )}

                {/* Settings Icon: the app's settings surface is Data & Privacy */}
                <Link
                  to="/privacy"
                  className="p-2 rounded-lg hover:bg-gunmetal/20 transition-colors"
                  title="Data & Privacy"
                  aria-label="Data and privacy settings"
                >
                  <Settings className="w-5 h-5 text-muted-gold hover:text-bright-gold transition-colors" />
                </Link>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gunmetal/20 transition-colors"
                  title="Log Out"
                >
                  <LogOut className="w-5 h-5 text-muted-gold" />
                  <span className="text-pearl hover:text-bright-gold transition-colors">Log Out</span>
                </button>
              </>
            ) : (
              <Button
                onClick={() => redirectToAuth('signin')}
                variant="secondary"
              >
                Log In
              </Button>
            )}
          </div>

          {/* Mobile Menu Button: not in the iOS app, where the tab bar replaces the drawer */}
          {!Capacitor.isNativePlatform() && (
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;